import { useState, useEffect, useCallback } from 'react';
import {
  Percent,
  Loader2,
  Search,
  CheckCircle,
  Clock,
  DollarSign,
  XCircle,
  TrendingUp,
  Users,
  Download,
  Settings,
  Trash2,
  Save,
  ChevronDown,
  ChevronUp,
  CalendarIcon
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { useNotifications } from '../store/useStore';
import { useAuth } from '../hooks/useAuth';
import { supabase } from '../lib/supabase';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';

// ── Types ──────────────────────────────────────────────────

interface Commission {
  id: string;
  user_id: string;
  price_list_id: string;
  customer_id: string | null;
  base_amount: number;
  rate_percentage: number;
  commission_amount: number;
  status: 'pending' | 'approved' | 'paid' | 'cancelled';
  notes: string | null;
  approved_at: string | null;
  approved_by: string | null;
  paid_at: string | null;
  created_at: string | null;
  updated_at: string | null;
  // joined
  user_name?: string;
  user_email?: string;
  customer_name?: string;
  price_list_name?: string;
}

interface CommissionRate {
  id: string;
  user_id: string;
  rate_percentage: number;
  valid_from: string;
  valid_until: string | null;
  user_name?: string;
  user_email?: string;
}

interface KPIData {
  totalCommissions: number;
  pendingAmount: number;
  approvedAmount: number;
  paidAmount: number;
  totalCount: number;
}

// ── Helpers ────────────────────────────────────────────────

const formatCurrency = (amount: number) =>
  new Intl.NumberFormat('it-IT', { style: 'currency', currency: 'EUR' }).format(amount);

const formatDate = (date: string | null) =>
  date ? new Date(date).toLocaleDateString('it-IT') : '-';

const statusConfig = {
  pending: { label: 'In attesa', color: 'bg-yellow-100 text-yellow-800', icon: Clock },
  approved: { label: 'Approvata', color: 'bg-blue-100 text-blue-800', icon: CheckCircle },
  paid: { label: 'Pagata', color: 'bg-green-100 text-green-800', icon: DollarSign },
  cancelled: { label: 'Annullata', color: 'bg-red-100 text-red-800', icon: XCircle },
};

// ── Component ──────────────────────────────────────────────

export const CommissionsPage = () => {
  const [commissions, setCommissions] = useState<Commission[]>([]);
  const [rates, setRates] = useState<CommissionRate[]>([]);
  const [commerciali, setCommerciali] = useState<{ id: string; full_name: string | null; email: string; role: string }[]>([]);
  const [kpi, setKpi] = useState<KPIData>({ totalCommissions: 0, pendingAmount: 0, approvedAmount: 0, paidAmount: 0, totalCount: 0 });
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [commercialeFilter, setCommercialeFilter] = useState<string>('all');
  const [monthFilter, setMonthFilter] = useState<string>('all');
  const [showRatesSection, setShowRatesSection] = useState(false);
  const [editingRates, setEditingRates] = useState<Record<string, string>>({});
  const [savingRateFor, setSavingRateFor] = useState<string | null>(null);
  const [sortField, setSortField] = useState<'created_at' | 'commission_amount' | 'base_amount'>('created_at');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');

  const { addNotification } = useNotifications();
  const { user, profile } = useAuth();

  const isAdmin = profile?.role === 'admin' || profile?.role === 'amministrazione';

  // ── Load data ─────────────────────────────────────────

  const loadCommerciali = useCallback(async () => {
    if (!isAdmin) return;
    const { data } = await supabase
      .from('profiles')
      .select('id, full_name, email, role')
      .in('role', ['commerciale', 'sales', 'amministrazione', 'admin'])
      .order('full_name');
    if (data) setCommerciali(data as any);
  }, [isAdmin]);

  // Sync: genera provvigioni mancanti per offerte attive con tasso configurato
  const syncMissingCommissions = useCallback(async () => {
    try {
      // 1. Carica tutte le offerte attive con i loro items
      const { data: activePriceLists } = await (supabase
        .from('price_lists') as any)
        .select('id, name, created_by, customer_id, price_list_items(price, min_quantity, discount_percentage)')
        .eq('is_active', true);

      if (!activePriceLists || activePriceLists.length === 0) return;

      // 2. Carica provvigioni esistenti per sapere quali mancano
      const { data: existingCommissions } = await supabase
        .from('commissions')
        .select('price_list_id');

      const existingPriceListIds = new Set((existingCommissions || []).map(c => c.price_list_id));

      // 3. Carica tutti i tassi attivi
      const today = new Date().toISOString().split('T')[0];
      const { data: activeRates } = await supabase
        .from('commission_rates')
        .select('user_id, rate_percentage')
        .lte('valid_from', today)
        .or(`valid_until.is.null,valid_until.gte.${today}`);

      if (!activeRates || activeRates.length === 0) return;

      // Mappa user_id → tasso più recente
      const ratesByUser = new Map<string, number>();
      activeRates.forEach(r => {
        // Prendi il primo (più recente per valid_from DESC) oppure sovrascrivi
        if (!ratesByUser.has(r.user_id)) {
          ratesByUser.set(r.user_id, Number(r.rate_percentage));
        }
      });

      // 4. Per ogni offerta attiva senza provvigione, genera il record
      // Per quelle con provvigione pending, aggiorna importi se cambiati
      const toInsert: any[] = [];
      const toUpdate: { id: string; data: any }[] = [];

      // Carica anche provvigioni esistenti con dettagli per aggiornamento
      const { data: fullExisting } = await supabase
        .from('commissions')
        .select('id, price_list_id, user_id, base_amount, rate_percentage, status');

      const existingByPriceList = new Map<string, any>();
      (fullExisting || []).forEach(c => existingByPriceList.set(c.price_list_id, c));

      for (const pl of activePriceLists) {
        const rate = ratesByUser.get(pl.created_by);
        if (rate === undefined) continue;

        const items = pl.price_list_items || [];
        const baseAmount = Math.round(items.reduce((sum: number, item: any) => {
          const price = Number(item.price) || 0;
          const qty = Number(item.min_quantity) || 1;
          const discount = Number(item.discount_percentage) || 0;
          return sum + price * qty * (1 - discount / 100);
        }, 0) * 100) / 100;

        const commissionAmount = Math.round(baseAmount * rate / 100 * 100) / 100;

        const existing = existingByPriceList.get(pl.id);
        if (!existing) {
          // Nuova provvigione
          toInsert.push({
            user_id: pl.created_by,
            price_list_id: pl.id,
            customer_id: pl.customer_id || null,
            base_amount: baseAmount,
            rate_percentage: rate,
            commission_amount: commissionAmount,
            status: 'pending',
          });
        } else if (existing.status === 'pending') {
          // Aggiorna se importo base, tasso o commerciale sono cambiati
          const needsUpdate =
            Math.abs(Number(existing.base_amount) - baseAmount) > 0.01 ||
            Math.abs(Number(existing.rate_percentage) - rate) > 0.01 ||
            existing.user_id !== pl.created_by;

          if (needsUpdate) {
            toUpdate.push({
              id: existing.id,
              data: {
                user_id: pl.created_by,
                customer_id: pl.customer_id || null,
                base_amount: baseAmount,
                rate_percentage: rate,
                commission_amount: commissionAmount,
                updated_at: new Date().toISOString(),
              },
            });
          }
        }
      }

      if (toInsert.length > 0) {
        await supabase.from('commissions').insert(toInsert);
      }
      for (const upd of toUpdate) {
        await supabase.from('commissions').update(upd.data).eq('id', upd.id);
      }
    } catch (error) {
      console.error('Error syncing commissions:', error);
    }
  }, []);

  const loadCommissions = useCallback(async () => {
    try {
      setLoading(true);
      if (!user?.id) return;

      // Prima sincronizza le provvigioni mancanti
      await syncMissingCommissions();

      const { data: commissionsData, error } = await supabase
        .from('commissions')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;
      if (!commissionsData) { setCommissions([]); return; }

      // Fetch related data
      const userIds = [...new Set(commissionsData.map(c => c.user_id))];
      const customerIds = [...new Set(commissionsData.map(c => c.customer_id).filter(Boolean))] as string[];
      const priceListIds = [...new Set(commissionsData.map(c => c.price_list_id))];

      const [profilesRes, customersRes, priceListsRes] = await Promise.all([
        userIds.length > 0 ? supabase.from('profiles').select('id, full_name, email').in('id', userIds) : { data: [] },
        customerIds.length > 0 ? supabase.from('customers').select('id, company_name').in('id', customerIds) : { data: [] },
        priceListIds.length > 0 ? supabase.from('price_lists').select('id, name').in('id', priceListIds) : { data: [] },
      ]);

      const profiles = profilesRes.data || [];
      const customers = customersRes.data || [];
      const priceLists = priceListsRes.data || [];

      const enriched: Commission[] = commissionsData.map(c => {
        const prof = profiles.find(p => p.id === c.user_id);
        const cust = customers.find(cu => cu.id === c.customer_id);
        const pl = priceLists.find(p => p.id === c.price_list_id);
        return {
          ...c,
          user_name: prof?.full_name || prof?.email?.split('@')[0] || 'N/A',
          user_email: prof?.email || '',
          customer_name: cust?.company_name || '-',
          price_list_name: pl?.name || '-',
        };
      });

      setCommissions(enriched);

      // KPI
      const pending = enriched.filter(c => c.status === 'pending').reduce((s, c) => s + Number(c.commission_amount), 0);
      const approved = enriched.filter(c => c.status === 'approved').reduce((s, c) => s + Number(c.commission_amount), 0);
      const paid = enriched.filter(c => c.status === 'paid').reduce((s, c) => s + Number(c.commission_amount), 0);
      setKpi({
        totalCommissions: pending + approved + paid,
        pendingAmount: pending,
        approvedAmount: approved,
        paidAmount: paid,
        totalCount: enriched.length,
      });

    } catch (error) {
      console.error('Error loading commissions:', error);
      addNotification({ type: 'error', title: 'Errore', message: 'Impossibile caricare le provvigioni' });
    } finally {
      setLoading(false);
    }
  }, [user?.id, addNotification, syncMissingCommissions]);

  const loadRates = useCallback(async () => {
    try {
      const { data, error } = await supabase
        .from('commission_rates')
        .select('*')
        .order('valid_from', { ascending: false });

      if (error) throw error;

      if (data) {
        // Enrich with user names
        const userIds = [...new Set(data.map(r => r.user_id))];
        const { data: profiles } = await supabase.from('profiles').select('id, full_name, email').in('id', userIds);

        const enriched: CommissionRate[] = data.map(r => {
          const prof = profiles?.find(p => p.id === r.user_id);
          return {
            ...r,
            user_name: prof?.full_name || prof?.email?.split('@')[0] || 'N/A',
            user_email: prof?.email || '',
          };
        });
        setRates(enriched);
      }
    } catch (error) {
      console.error('Error loading rates:', error);
    }
  }, []);

  useEffect(() => {
    loadCommissions();
    loadRates();
    loadCommerciali();
  }, [loadCommissions, loadRates, loadCommerciali]);

  // ── Actions ─────────────────────────────────────────

  const updateCommissionStatus = async (commissionId: string, newStatus: 'approved' | 'paid' | 'cancelled') => {
    try {
      const updates: Record<string, any> = { status: newStatus, updated_at: new Date().toISOString() };
      if (newStatus === 'approved') {
        updates.approved_at = new Date().toISOString();
        updates.approved_by = user?.id;
      }
      if (newStatus === 'paid') {
        updates.paid_at = new Date().toISOString();
      }

      const { error } = await supabase.from('commissions').update(updates).eq('id', commissionId);
      if (error) throw error;

      addNotification({ type: 'success', title: 'Aggiornato', message: `Provvigione ${statusConfig[newStatus].label.toLowerCase()}` });
      await loadCommissions();
    } catch (error: any) {
      addNotification({ type: 'error', title: 'Errore', message: error.message || 'Impossibile aggiornare lo stato' });
    }
  };

  const getCurrentRateForUser = (userId: string) => {
    const today = new Date().toISOString().split('T')[0];
    return rates.find(r =>
      r.user_id === userId &&
      r.valid_from <= today &&
      (!r.valid_until || r.valid_until >= today)
    );
  };

  const saveRateForUser = async (userId: string) => {
    const value = editingRates[userId];
    if (value === undefined || value === '') return;
    setSavingRateFor(userId);
    try {
      const currentRate = getCurrentRateForUser(userId);
      const newPercentage = parseFloat(value);

      if (currentRate) {
        // Chiudi il tasso corrente e creane uno nuovo
        await supabase.from('commission_rates').update({ valid_until: new Date().toISOString().split('T')[0] }).eq('id', currentRate.id);
      }

      const { error } = await supabase.from('commission_rates').insert({
        user_id: userId,
        rate_percentage: newPercentage,
        valid_from: new Date().toISOString().split('T')[0],
      });
      if (error) throw error;

      addNotification({ type: 'success', title: 'Salvato', message: 'Tasso provvigione aggiornato' });
      setEditingRates(prev => { const n = { ...prev }; delete n[userId]; return n; });
      await loadRates();
    } catch (error: any) {
      addNotification({ type: 'error', title: 'Errore', message: error.message || 'Impossibile salvare il tasso' });
    } finally {
      setSavingRateFor(null);
    }
  };

  const removeRateForUser = async (userId: string) => {
    const currentRate = getCurrentRateForUser(userId);
    if (!currentRate) return;
    try {
      const { error } = await supabase.from('commission_rates').update({ valid_until: new Date().toISOString().split('T')[0] }).eq('id', currentRate.id);
      if (error) throw error;
      addNotification({ type: 'success', title: 'Rimosso', message: 'Tasso provvigione disattivato' });
      await loadRates();
    } catch (error: any) {
      addNotification({ type: 'error', title: 'Errore', message: error.message || 'Impossibile rimuovere il tasso' });
    }
  };

  // ── Export ─────────────────────────────────────────

  const exportToExcel = () => {
    const rows = filtered.map(c => ({
      'Commerciale': c.user_name,
      'Offerta': c.price_list_name,
      'Cliente': c.customer_name,
      'Importo Base': Number(c.base_amount),
      '% Provvigione': Number(c.rate_percentage),
      'Provvigione': Number(c.commission_amount),
      'Stato': statusConfig[c.status].label,
      'Data': formatDate(c.created_at),
      'Data Approvazione': formatDate(c.approved_at),
      'Data Pagamento': formatDate(c.paid_at),
    }));

    const ws = XLSX.utils.json_to_sheet(rows);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Provvigioni');

    // Summary sheet
    const summary = [
      { 'Metrica': 'Totale Provvigioni', 'Valore': kpi.totalCommissions },
      { 'Metrica': 'In Attesa', 'Valore': kpi.pendingAmount },
      { 'Metrica': 'Approvate', 'Valore': kpi.approvedAmount },
      { 'Metrica': 'Pagate', 'Valore': kpi.paidAmount },
      { 'Metrica': 'Numero Provvigioni', 'Valore': kpi.totalCount },
    ];
    const wsSummary = XLSX.utils.json_to_sheet(summary);
    XLSX.utils.book_append_sheet(wb, wsSummary, 'Riepilogo');

    const excelBuffer = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
    const blob = new Blob([excelBuffer], { type: 'application/octet-stream' });
    const monthLabel = monthFilter !== 'all' ? `_${monthFilter}` : '';
    saveAs(blob, `provvigioni${monthLabel}_${new Date().toISOString().split('T')[0]}.xlsx`);
  };

  // ── Filtering & Sorting ───────────────────────────

  const getMonthOptions = () => {
    const months = new Set<string>();
    commissions.forEach(c => {
      if (c.created_at) {
        const d = new Date(c.created_at);
        months.add(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`);
      }
    });
    return Array.from(months).sort().reverse();
  };

  const filtered = commissions
    .filter(c => {
      const search = searchTerm.toLowerCase();
      const matchesSearch = !search ||
        (c.user_name?.toLowerCase() || '').includes(search) ||
        (c.customer_name?.toLowerCase() || '').includes(search) ||
        (c.price_list_name?.toLowerCase() || '').includes(search);
      const matchesStatus = statusFilter === 'all' || c.status === statusFilter;
      const matchesCommerciale = commercialeFilter === 'all' || c.user_id === commercialeFilter;
      const matchesMonth = monthFilter === 'all' || (c.created_at && c.created_at.startsWith(monthFilter));
      return matchesSearch && matchesStatus && matchesCommerciale && matchesMonth;
    })
    .sort((a, b) => {
      const aVal = sortField === 'created_at' ? (a.created_at || '') : Number(a[sortField]);
      const bVal = sortField === 'created_at' ? (b.created_at || '') : Number(b[sortField]);
      if (aVal < bVal) return sortDir === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortDir === 'asc' ? 1 : -1;
      return 0;
    });

  const toggleSort = (field: typeof sortField) => {
    if (sortField === field) {
      setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDir('desc');
    }
  };

  const SortIcon = ({ field }: { field: typeof sortField }) => {
    if (sortField !== field) return null;
    return sortDir === 'asc' ? <ChevronUp className="w-3 h-3 inline ml-1" /> : <ChevronDown className="w-3 h-3 inline ml-1" />;
  };

  // ── Render ────────────────────────────────────────

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <Loader2 className="w-8 h-8 animate-spin text-gray-400 mx-auto mb-4" />
          <p className="text-gray-600">Caricamento provvigioni...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4 md:space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-gray-900">Provvigioni</h1>
          <p className="text-sm text-gray-600 mt-1">
            {isAdmin ? 'Gestisci le provvigioni di tutti i commerciali' : 'Le tue provvigioni sulle vendite'}
          </p>
        </div>
        <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 w-full sm:w-auto">
          <Button variant="outline" onClick={exportToExcel}>
            <Download className="w-4 h-4 mr-2" />
            Esporta Excel
          </Button>
          {isAdmin && (
            <Button onClick={() => setShowRatesSection(!showRatesSection)} variant={showRatesSection ? 'default' : 'outline'}>
              <Settings className="w-4 h-4 mr-2" />
              {showRatesSection ? 'Nascondi Tassi' : 'Gestisci Tassi'}
            </Button>
          )}
        </div>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 md:gap-6">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between gap-2 space-y-0 p-4 md:p-6 pb-2 md:pb-2">
            <CardTitle className="text-sm font-medium text-gray-600">Totale Provvigioni</CardTitle>
            <TrendingUp className="h-4 w-4 text-emerald-500" />
          </CardHeader>
          <CardContent className="p-4 md:p-6 pt-0 md:pt-0">
            <div className="text-lg md:text-2xl font-bold">{formatCurrency(kpi.totalCommissions)}</div>
            <p className="text-xs text-gray-500 mt-1">{kpi.totalCount} provvigioni</p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between gap-2 space-y-0 p-4 md:p-6 pb-2 md:pb-2">
            <CardTitle className="text-sm font-medium text-gray-600">In Attesa</CardTitle>
            <Clock className="h-4 w-4 text-yellow-500" />
          </CardHeader>
          <CardContent className="p-4 md:p-6 pt-0 md:pt-0">
            <div className="text-lg md:text-2xl font-bold text-yellow-600">{formatCurrency(kpi.pendingAmount)}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between gap-2 space-y-0 p-4 md:p-6 pb-2 md:pb-2">
            <CardTitle className="text-sm font-medium text-gray-600">Approvate</CardTitle>
            <CheckCircle className="h-4 w-4 text-blue-500" />
          </CardHeader>
          <CardContent className="p-4 md:p-6 pt-0 md:pt-0">
            <div className="text-lg md:text-2xl font-bold text-blue-600">{formatCurrency(kpi.approvedAmount)}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between gap-2 space-y-0 p-4 md:p-6 pb-2 md:pb-2">
            <CardTitle className="text-sm font-medium text-gray-600">Pagate</CardTitle>
            <DollarSign className="h-4 w-4 text-green-500" />
          </CardHeader>
          <CardContent className="p-4 md:p-6 pt-0 md:pt-0">
            <div className="text-lg md:text-2xl font-bold text-green-600">{formatCurrency(kpi.paidAmount)}</div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-3 md:gap-4">
        <Card className="md:col-span-1">
          <CardContent className="p-4 md:p-6">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
              <Input
                placeholder="Cerca..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 md:p-6">
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger>
                <SelectValue placeholder="Stato" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tutti gli stati</SelectItem>
                <SelectItem value="pending">In attesa</SelectItem>
                <SelectItem value="approved">Approvate</SelectItem>
                <SelectItem value="paid">Pagate</SelectItem>
                <SelectItem value="cancelled">Annullate</SelectItem>
              </SelectContent>
            </Select>
          </CardContent>
        </Card>
        {isAdmin && (
          <Card>
            <CardContent className="p-4 md:p-6">
              <Select value={commercialeFilter} onValueChange={setCommercialeFilter}>
                <SelectTrigger>
                  <SelectValue placeholder="Commerciale" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tutti i commerciali</SelectItem>
                  {commerciali.map(c => (
                    <SelectItem key={c.id} value={c.id}>{c.full_name || c.email}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </CardContent>
          </Card>
        )}
        <Card>
          <CardContent className="p-4 md:p-6">
            <Select value={monthFilter} onValueChange={setMonthFilter}>
              <SelectTrigger>
                <CalendarIcon className="w-4 h-4 mr-2 text-gray-400" />
                <SelectValue placeholder="Periodo" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tutti i periodi</SelectItem>
                {getMonthOptions().map(m => {
                  const [y, mo] = m.split('-');
                  const label = new Date(parseInt(y), parseInt(mo) - 1).toLocaleDateString('it-IT', { month: 'long', year: 'numeric' });
                  return <SelectItem key={m} value={m}>{label}</SelectItem>;
                })}
              </SelectContent>
            </Select>
          </CardContent>
        </Card>
      </div>

      {/* Commissions Table */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-lg md:text-2xl">
            <Percent className="w-5 h-5" />
            <span>Provvigioni ({filtered.length})</span>
          </CardTitle>
        </CardHeader>
        <CardContent>
          {filtered.length === 0 ? (
            <div className="text-center py-8">
              <Percent className="w-12 h-12 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-500">Nessuna provvigione trovata</p>
            </div>
          ) : (
            <div className="table-scroll">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b text-left text-gray-600">
                    {isAdmin && <th className="pb-3 pr-4 font-medium">Commerciale</th>}
                    <th className="pb-3 pr-4 font-medium">Offerta</th>
                    <th className="pb-3 pr-4 font-medium">Cliente</th>
                    <th className="pb-3 pr-4 font-medium cursor-pointer select-none" onClick={() => toggleSort('base_amount')}>
                      Importo Base <SortIcon field="base_amount" />
                    </th>
                    <th className="pb-3 pr-4 font-medium">%</th>
                    <th className="pb-3 pr-4 font-medium cursor-pointer select-none" onClick={() => toggleSort('commission_amount')}>
                      Provvigione <SortIcon field="commission_amount" />
                    </th>
                    <th className="pb-3 pr-4 font-medium">Stato</th>
                    <th className="pb-3 pr-4 font-medium cursor-pointer select-none" onClick={() => toggleSort('created_at')}>
                      Data <SortIcon field="created_at" />
                    </th>
                    {isAdmin && <th className="pb-3 font-medium">Azioni</th>}
                  </tr>
                </thead>
                <tbody>
                  {filtered.map(c => {
                    const cfg = statusConfig[c.status];
                    return (
                      <tr key={c.id} className="border-b last:border-b-0 hover:bg-gray-50">
                        {isAdmin && (
                          <td className="py-3 pr-4">
                            <div className="font-medium text-gray-900">{c.user_name}</div>
                          </td>
                        )}
                        <td className="py-3 pr-4 text-gray-700">{c.price_list_name}</td>
                        <td className="py-3 pr-4 text-gray-600">{c.customer_name}</td>
                        <td className="py-3 pr-4 text-gray-700 font-medium">{formatCurrency(Number(c.base_amount))}</td>
                        <td className="py-3 pr-4 text-gray-600">{Number(c.rate_percentage)}%</td>
                        <td className="py-3 pr-4 font-semibold text-emerald-700">{formatCurrency(Number(c.commission_amount))}</td>
                        <td className="py-3 pr-4">
                          <span className={`px-2 py-1 rounded-full text-xs font-medium inline-flex items-center gap-1 ${cfg.color}`}>
                            <cfg.icon className="w-3 h-3" />
                            {cfg.label}
                          </span>
                        </td>
                        <td className="py-3 pr-4 text-gray-500 text-xs">{formatDate(c.created_at)}</td>
                        {isAdmin && (
                          <td className="py-3">
                            <div className="flex items-center gap-1">
                              {c.status === 'pending' && (
                                <>
                                  <Button size="sm" variant="outline" className="text-blue-600 h-7 text-xs" onClick={() => updateCommissionStatus(c.id, 'approved')}>
                                    Approva
                                  </Button>
                                  <Button size="sm" variant="outline" className="text-red-600 h-7 text-xs" onClick={() => updateCommissionStatus(c.id, 'cancelled')}>
                                    Annulla
                                  </Button>
                                </>
                              )}
                              {c.status === 'approved' && (
                                <Button size="sm" variant="outline" className="text-green-600 h-7 text-xs" onClick={() => updateCommissionStatus(c.id, 'paid')}>
                                  Segna pagata
                                </Button>
                              )}
                            </div>
                          </td>
                        )}
                      </tr>
                    );
                  })}
                </tbody>
              </table>

              {/* Totals row */}
              <div className="mt-4 pt-4 border-t flex flex-wrap justify-end gap-x-8 gap-y-2 text-sm">
                <div className="text-gray-600">
                  Totale importo base: <span className="font-semibold text-gray-900">{formatCurrency(filtered.reduce((s, c) => s + Number(c.base_amount), 0))}</span>
                </div>
                <div className="text-gray-600">
                  Totale provvigioni: <span className="font-semibold text-emerald-700">{formatCurrency(filtered.reduce((s, c) => s + Number(c.commission_amount), 0))}</span>
                </div>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Admin: Riepilogo per commerciale */}
      {isAdmin && commissions.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-lg md:text-2xl">
              <Users className="w-5 h-5" />
              <span>Riepilogo per Commerciale</span>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="table-scroll">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b text-left text-gray-600">
                    <th className="pb-3 pr-4 font-medium">Commerciale</th>
                    <th className="pb-3 pr-4 font-medium">N. Offerte</th>
                    <th className="pb-3 pr-4 font-medium">Fatturato Generato</th>
                    <th className="pb-3 pr-4 font-medium">% Media</th>
                    <th className="pb-3 pr-4 font-medium">Provvigioni Totali</th>
                    <th className="pb-3 pr-4 font-medium">In Attesa</th>
                    <th className="pb-3 pr-4 font-medium">Pagate</th>
                  </tr>
                </thead>
                <tbody>
                  {(() => {
                    const byUser = new Map<string, { name: string; count: number; baseTotal: number; commTotal: number; rateSum: number; pending: number; paid: number }>();
                    commissions.forEach(c => {
                      const existing = byUser.get(c.user_id) || { name: c.user_name || 'N/A', count: 0, baseTotal: 0, commTotal: 0, rateSum: 0, pending: 0, paid: 0 };
                      existing.count++;
                      existing.baseTotal += Number(c.base_amount);
                      existing.commTotal += Number(c.commission_amount);
                      existing.rateSum += Number(c.rate_percentage);
                      if (c.status === 'pending') existing.pending += Number(c.commission_amount);
                      if (c.status === 'paid') existing.paid += Number(c.commission_amount);
                      byUser.set(c.user_id, existing);
                    });

                    return Array.from(byUser.entries()).map(([userId, data]) => (
                      <tr key={userId} className="border-b last:border-b-0 hover:bg-gray-50">
                        <td className="py-3 pr-4 font-medium text-gray-900">{data.name}</td>
                        <td className="py-3 pr-4 text-gray-700">{data.count}</td>
                        <td className="py-3 pr-4 text-gray-700">{formatCurrency(data.baseTotal)}</td>
                        <td className="py-3 pr-4 text-gray-600">{(data.rateSum / data.count).toFixed(1)}%</td>
                        <td className="py-3 pr-4 font-semibold text-emerald-700">{formatCurrency(data.commTotal)}</td>
                        <td className="py-3 pr-4 text-yellow-600">{formatCurrency(data.pending)}</td>
                        <td className="py-3 pr-4 text-green-600">{formatCurrency(data.paid)}</td>
                      </tr>
                    ));
                  })()}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Rates Management Section (Admin only) */}
      {isAdmin && showRatesSection && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-lg md:text-2xl">
              <Settings className="w-5 h-5" />
              <span>Tassi Provvigione per Account</span>
            </CardTitle>
            <p className="text-sm text-gray-500 mt-1">
              Imposta la percentuale di provvigione individuale per ogni account. Il tasso viene applicato automaticamente alle nuove offerte attivate.
            </p>
          </CardHeader>
          <CardContent>
            <div className="table-scroll">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b text-left text-gray-600">
                    <th className="pb-3 pr-4 font-medium">Account</th>
                    <th className="pb-3 pr-4 font-medium">Email</th>
                    <th className="pb-3 pr-4 font-medium">Ruolo</th>
                    <th className="pb-3 pr-4 font-medium">Tasso Attuale</th>
                    <th className="pb-3 pr-4 font-medium">Nuovo Tasso %</th>
                    <th className="pb-3 font-medium">Azioni</th>
                  </tr>
                </thead>
                <tbody>
                  {commerciali.map(c => {
                    const currentRate = getCurrentRateForUser(c.id);
                    const isEditing = editingRates[c.id] !== undefined;
                    const isSaving = savingRateFor === c.id;
                    const roleLabels: Record<string, string> = {
                      commerciale: 'Commerciale',
                      sales: 'Sales',
                      amministrazione: 'Amministrazione',
                      admin: 'Admin',
                    };
                    return (
                      <tr key={c.id} className="border-b last:border-b-0 hover:bg-gray-50">
                        <td className="py-3 pr-4 font-medium text-gray-900">{c.full_name || c.email.split('@')[0]}</td>
                        <td className="py-3 pr-4 text-gray-500 text-xs">{c.email}</td>
                        <td className="py-3 pr-4">
                          <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-700">
                            {roleLabels[c.role] || c.role}
                          </span>
                        </td>
                        <td className="py-3 pr-4">
                          {currentRate ? (
                            <span className="text-lg font-bold text-emerald-600">{currentRate.rate_percentage}%</span>
                          ) : (
                            <span className="text-gray-400 text-xs">Non impostato</span>
                          )}
                        </td>
                        <td className="py-3 pr-4 w-32">
                          <Input
                            type="number"
                            step="0.5"
                            min="0"
                            max="100"
                            placeholder={currentRate ? `${currentRate.rate_percentage}` : '0'}
                            value={editingRates[c.id] ?? ''}
                            onChange={(e) => setEditingRates(prev => ({ ...prev, [c.id]: e.target.value }))}
                            className="h-8 w-24"
                          />
                        </td>
                        <td className="py-3">
                          <div className="flex items-center gap-1">
                            <Button
                              size="sm"
                              variant="outline"
                              className="h-7 text-xs text-emerald-600"
                              disabled={!isEditing || isSaving}
                              onClick={() => saveRateForUser(c.id)}
                            >
                              {isSaving ? <Loader2 className="w-3 h-3 animate-spin" /> : <Save className="w-3 h-3 mr-1" />}
                              Salva
                            </Button>
                            {currentRate && (
                              <Button
                                size="sm"
                                variant="ghost"
                                className="h-7 text-xs text-red-500"
                                onClick={() => removeRateForUser(c.id)}
                              >
                                <Trash2 className="w-3 h-3" />
                              </Button>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              {commerciali.length === 0 && (
                <p className="text-center text-gray-500 py-4">Nessun account commerciale trovato</p>
              )}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default CommissionsPage;
